import React from "react";
import { Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTenant } from "../../../app/providers/TenantProvider";

const COLORS = {
  primary: "#1f4e79",
  success: "#1f9d6b",
  warning: "#e0a100",
  danger: "#c1121f",
  info: "#198ae3",
};

/**
 * StatCard — a compact KPI tile: colored icon + value + label.
 * Pass `to` to make the whole tile a shortcut link.
 */
function StatCard({ label, value, sub, icon, variant = "primary", to }) {
  const color = COLORS[variant] ?? COLORS.primary;
  const body = (
    <Card className="h-100 shadow-sm border-0">
      {/* Apilado en móvil (el valor ocupa todo el ancho de la card, sin que el
          ícono le robe espacio y sin partir el monto); horizontal en ≥sm. */}
      <Card.Body className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 gap-sm-3 py-3">
        <div
          className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            background: `${color}22`,
            color,
            fontSize: 22,
          }}
        >
          <i className={`mdi ${icon}`} aria-hidden="true" />
        </div>
        <div style={{ minWidth: 0 }} className="w-100">
          <div
            className="fw-bold text-nowrap"
            style={{ fontSize: "clamp(1rem, 5vw, 1.5rem)", lineHeight: 1.15 }}
          >
            {value}
          </div>
          <div className="text-muted small">{label}</div>
          {sub && (
            <div className="text-muted" style={{ fontSize: ".72rem" }}>
              {sub}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  if (!to) return body;
  return (
    <Link to={to} className="text-decoration-none text-reset d-block h-100">
      {body}
    </Link>
  );
}

/** QuickAction — a shortcut button for the dashboard action grid. */
function QuickAction({ to, icon, label, variant = "outline-primary" }) {
  return (
    <Link
      to={to}
      className={`btn btn-${variant} d-flex align-items-center gap-2 justify-content-center w-100`}
      style={{ minHeight: 48 }}
    >
      <i className={`mdi ${icon}`} aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
}

/** Section heading used between dashboard blocks. */
function SectionTitle({ children }) {
  return (
    <h6 className="text-uppercase text-muted small fw-semibold mt-4 mb-2">
      {children}
    </h6>
  );
}

/**
 * DashboardGreeting — encabezado "Hola, {name}" compartido por los tres
 * dashboards (owner/teacher/student). Si la academia cargó un logo
 * (Company.logo, subido desde Mi perfil), se muestra a la izquierda del
 * saludo, empujando el texto — junto a la foto de perfil del navbar casi no
 * se notaba (feedback real de uso); aquí, con más espacio, sí. Sin logo
 * cargado no se reserva ni se dibuja nada — mismo saludo de siempre.
 *
 * `actions` (opcional) — nodo renderizado a la derecha del saludo (p.ej. el
 * botón "Copiar enlace de inscripción" de OwnerDashboard). Ningún dashboard
 * está obligado a pasarlo; sin él, el layout es idéntico al de siempre.
 */
function DashboardGreeting({ name, subtitle, actions }) {
  const { branding } = useTenant();
  const logoUrl = branding?.logo_url;

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
      <div className="d-flex align-items-center gap-3">
        {logoUrl && (
          <img
            src={logoUrl}
            alt={branding?.name ? `Logo de ${branding.name}` : "Logo de la academia"}
            className="flex-shrink-0"
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              objectFit: "contain",
              background: "#fff",
              border: "1px solid var(--kp-border)",
              padding: 4,
            }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <h4 className="mb-1">Hola, {name} 👋</h4>
          <p className="text-muted mb-0">{subtitle}</p>
        </div>
      </div>
      {actions && <div className="flex-shrink-0">{actions}</div>}
    </div>
  );
}

export { StatCard, QuickAction, SectionTitle, DashboardGreeting };
